/** The HUD root: the lobby, then the HUD, the panels, the sizes sheet and the toasts over the world. */
import { useEffect, useState, type JSX } from 'react';

import type { Game } from '@game/game';
import type { Panel } from '@game/types';
import { openPanel } from '@game/view/panels';

import { AwayPanel } from './features/away/AwayPanel';
import { BuildMenu } from './features/build/BuildMenu';
import { DevOverlay } from './features/dev/DevOverlay';
import { HubPanel } from './features/hub/HubPanel';
import { Hud } from './features/hud/Hud';
import { InfoSheet } from './features/info/InfoSheet';
import { LoadSheet } from './features/load/LoadSheet';
import { Toasts } from './features/toasts/Toasts';
import { Welcome } from './features/welcome/Welcome';
import { WorldPanel } from './features/world/WorldPanel';
import { wireToasts } from './features/toasts/wire';
import { SHEET_LEAVE_MS, SheetLeaving } from './components/Sheet';
import { GameContext, useGameState } from './hooks';

import './theme/tokens.css';
import './theme/controls.css';

function Screens(): JSX.Element {
  const world = useGameState((s) => s.world);
  const hasView = useGameState((s) => s.view !== null);
  const panel = useGameState((s) => s.panel);
  const away = useGameState((s) => s.away);
  // a closing sheet stays a moment, sliding away: the shown panel follows the store with a delay
  const [shown, setShown] = useState<Panel>(panel);
  const [leaving, setLeaving] = useState(false);
  if (panel !== 'none' && (shown !== panel || leaving)) {
    setShown(panel);
    setLeaving(false);
  } else if (panel === 'none' && shown !== 'none' && !leaving) setLeaving(true);
  useEffect(() => {
    if (!leaving) return;
    const timer = setTimeout(() => {
      setShown('none');
      setLeaving(false);
    }, SHEET_LEAVE_MS);
    return () => clearTimeout(timer);
  }, [leaving]);
  return (
    <>
      {!world && <Welcome />}
      {world && hasView && <Hud />}
      <SheetLeaving value={leaving}>
        {shown === 'world' && world && <WorldPanel confirmLeave={false} />}
        {shown === 'leave' && world && <WorldPanel confirmLeave />}
        {shown === 'away' && away && <AwayPanel />}
        {shown === 'build' && hasView && <BuildMenu />}
        {shown === 'info' && hasView && <InfoSheet />}
        {shown === 'load' && hasView && <LoadSheet />}
        {shown === 'hub' && world && <HubPanel />}
      </SheetLeaving>
      <Toasts />
      {import.meta.env.DEV && <DevOverlay />}
    </>
  );
}

export function App({ game }: { game: Game }): JSX.Element {
  useEffect(() => wireToasts(game), [game]);
  useEffect(() => {
    let timer = 0;
    return game.store.subscribe((s, prev) => {
      if (s.away && !prev.away && s.view) {
        clearTimeout(timer);
        timer = window.setTimeout(() => {
          if (game.store.get().away && game.store.get().panel === 'none') openPanel(game, 'away');
        }, 1200);
      }
      if (s.away && !prev.view && s.view && s.panel === 'none') {
        clearTimeout(timer);
        timer = window.setTimeout(() => {
          if (game.store.get().away && game.store.get().panel === 'none') openPanel(game, 'away');
        }, 1200);
      }
    });
  }, [game]);
  return (
    <GameContext value={game}>
      <Screens />
    </GameContext>
  );
}
